import { ReactNode } from "react";

import Tooltip from "ds/components/Tooltip";
import { TooltipProps, TooltipReferenceProps } from "ds/components/Tooltip/types";
import { BillingTierFeature, Blueprint } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import useCanCreateBlueprint from "shared/Blueprint/access/useCanCreateBlueprint";
import useCanManageBlueprint from "shared/Blueprint/access/useCanManageBlueprint";
import { AccessResult } from "utils/access";
import useCanCreateStack from "shared/Stack/access/useCanCreateStack";

import { BlueprintActions } from "./types";

type BlueprintsFeatureGateTooltipProps = {
  on: (props: TooltipReferenceProps & { isDisabled: boolean }) => ReactNode;
  action: BlueprintActions;
  blueprint?: Blueprint;
  tooltipPlacement?: TooltipProps["placement"];
};

const getBlueprintFeatureGatingMessage = (action: BlueprintsFeatureGateTooltipProps["action"]) => {
  switch (action) {
    case BlueprintActions.CreateBlueprint:
      return "Blueprints are not supported by your plan. Upgrade to the higher plan to be able to create Blueprints.";
    case BlueprintActions.CreateStack:
      return "Blueprints are not supported by your plan. You may still preview or delete the blueprint that you created but you can not use it to create stacks.";
    case BlueprintActions.Edit:
      return "Blueprints are not supported by your plan. Upgrade to the higher plan to be able to edit the Blueprint.";
    case BlueprintActions.Publish:
      return "Blueprints are not supported by your plan. Upgrade to the higher plan to be able to publish the Blueprint.";
  }
};

const getBlueprintActionAccessResult = (
  action: BlueprintsFeatureGateTooltipProps["action"],
  canCreateBlueprint: AccessResult,
  canManageBlueprint: AccessResult,
  canCreateStack: AccessResult
) => {
  switch (action) {
    case BlueprintActions.CreateBlueprint:
      return canCreateBlueprint;
    case BlueprintActions.CreateStack:
      return canCreateStack;
    case BlueprintActions.Edit:
      return canManageBlueprint;
    case BlueprintActions.Publish:
      return canManageBlueprint;
  }
};

const BlueprintsFeatureGateTooltip = ({
  on,
  action,
  blueprint,
  tooltipPlacement,
}: BlueprintsFeatureGateTooltipProps) => {
  const isFeatureAvailableInPlan = useTierFeature(BillingTierFeature.Blueprints);
  const canCreateBlueprint = useCanCreateBlueprint();
  const canManageBlueprint = useCanManageBlueprint()(blueprint);
  const canCreateStack = useCanCreateStack();

  const accessResult = getBlueprintActionAccessResult(
    action,
    canCreateBlueprint,
    canManageBlueprint,
    canCreateStack
  );

  const isDisabled = !isFeatureAvailableInPlan || !accessResult.result;
  const isTooltipActive = !isFeatureAvailableInPlan || !!accessResult.message;

  return (
    <Tooltip
      on={(props) => on({ ...props, isDisabled })}
      active={isTooltipActive}
      widthMode="maxWidthSm"
      placement={tooltipPlacement}
    >
      {isFeatureAvailableInPlan ? accessResult.message : getBlueprintFeatureGatingMessage(action)}
    </Tooltip>
  );
};

export default BlueprintsFeatureGateTooltip;
